import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { useFeatureFlagEnabled, usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { FormKey } from '../../../enums/formKey';
import { PostHogEvent } from '../../../enums/postHogEvents';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useQuote } from '../../../store/selectors/useQuote';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { ApplianceCareTerms } from '../components/ApplianceCareTerms';
import { BenefitsTableAccordion } from '../components/BenefitsAccordionTable/BenefitsAccordionTable';
import { CallUsModal } from '../components/CallUsModal';
import { ChooseApplianceCareArticle } from '../components/ChooseApplianceCareArticle';
import { OfferBanner } from '../components/OfferBanner';
import { OfferPanelA } from '../components/OfferPanelA';
import { QuoteUpsellModal } from '../components/QuoteUpsellModal';
import { benefitsAccordionTableContent } from '../constants/benefitsAccordionTableContent';
import { chooseApplianceCareArticles } from '../constants/chooseApplianceCareArticles';

const styles = {
  container: 'flex flex-col items-center bg-bg-main',
  panel:
    'flex flex-col gap-space-400 max-w-206.25 w-full py-space-600 tablet:my-8 bg-white rounded-radius-200',
  guarantee:
    'flex flex-col gap-space-500 px-space-400 pb-space-200 tablet:px-space-600 tablet:pb-0',
  header: 'flex flex-col gap-space-500',
  benefits: 'px-space-400 tablet:pl-space-600 tablet:pr-space-800',
  checkout:
    'flex flex-col gap-space-600 px-space-400 pt-space-100 pb-2.5 tablet:gap-space-500 tablet:px-space-600 tablet:pt-space-600',
  offerRow: 'flex flex-col gap-space-400 tablet:flex-row tablet:items-start tablet:gap-space-700',
  terms: 'w-full tablet:w-86.25 shrink-0',
  // Primary CTA sits on top on mobile; DOM order stays outline → primary.
  buttonLayout: 'flex flex-col-reverse gap-space-600 tablet:flex-row tablet:gap-space-500',
  chooseApplianceCareContainer:
    'w-full flex flex-col justify-center items-center gap-2.5 p-space-500 mt-space-600 mb-space-800 bg-white tablet:bg-transparent',
  chooseApplianceCareArticles:
    'flex flex-col tablet:flex-row tablet:w-9/12 justify-center items-center gap-space-500',
};

/**
 * This is the control for Posthog in quote-page-v2-sep-2026 experiment.
 */
export function QuotePageA() {
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [displayModal, setDisplayModal] = useState(false);
  const posthog = usePostHog();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;

  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;
  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
      quoteId: quoteData.offer.offerReference,
      experimentName,
      experimentVariant,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);

  const captureQuoteEvent = (eventName: PostHogEvent) =>
    capturePostHogEvent(posthog, eventName, store.get(), {
      quoteId: quoteData.offer.offerReference,
    });

  const toggleModal = () => {
    captureQuoteEvent('protection-details-page-register-only-cta-clicked');

    setDisplayModal((prevDisplayModal) => {
      // Fire gaEvent only when the modal is being opened
      if (!prevDisplayModal) {
        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails',
          eventLabel: 'No, I want to register with warranty only',
          pagename: 'protection-details',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          offerPrice,
          quoteId: quoteData.offer.offerReference,
          experimentName,
          experimentVariant,
        });

        gaPageView({
          event: 'spa-pageview',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          offerPrice,
          applianceName,
          applianceCategory,
          appSessionID,
          experimentName,
          experimentVariant,
        });

        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails_popup_upsell',
          eventLabel: 'popup_presented',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          offerPrice,
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          quoteId: quoteData.offer.offerReference,
          experimentName,
          experimentVariant,
        });
      }
      return !prevDisplayModal;
    });
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    captureQuoteEvent('protection-details-modal-register-only-cta-clicked');
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
      experimentName,
      experimentVariant,
    });

    setDisplayModal(false);
  };

  const continueToCheckout = () => {
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    captureQuoteEvent(
      displayModal
        ? 'protection-details-modal-monthly-upsell-cta-clicked'
        : 'protection-details-page-monthly-upsell-cta-clicked',
    );

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displayModal ? 'protectionDetails_popup_upsell' : 'protectionDetails',
      eventLabel: 'Yes, I want this protection',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
      experimentName,
      experimentVariant,
    });

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      quoteId: quoteData.offer.offerReference,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.offer.offerReference,
          },
        ],
      },
    });
  };

  const offerPanelProps = {
    periodOfCover: discountTerms.periodOfCover,
    discountPercentage: discountTerms.discountPercentage,
    discountTerm: discountTerms.discountTerm,
    netFirstPaymentAmount: discountTerms.netFirstPaymentAmount,
    netFirstPaymentAmountAfterPromotionalPeriod:
      discountTerms.netFirstPaymentAmountAfterPromotionalPeriod,
    discountPaymentType: discountTerms.discountPaymentType,
    show2MonthsPromotion,
    brand,
  };

  return (
    <>
      <div className={styles.container} data-testid="quote-page-control">
        <div className={styles.panel}>
          <div className={styles.guarantee}>
            <OfferBanner
              discountTerms={discountTerms}
              show2MonthsPromotion={show2MonthsPromotion}
            />
            <div className={styles.header}>
              <h1 className="text-h1 text-text-headline">
                Your manufacturer's warranty doesn't cover everything - add extra coverage today
              </h1>
              <p className="text-body-large text-text-body">
                Get extra coverage now and avoid unexpected repair costs.
              </p>
            </div>
          </div>

          <div className={styles.benefits}>
            <BenefitsTableAccordion
              panels={benefitsAccordionTableContent}
              warrantyLength={quoteData?.items[0].guarantee ?? 12}
            />
          </div>

          <div className={styles.checkout}>
            <div className={styles.offerRow}>
              <div className={styles.terms}>
                <ApplianceCareTerms
                  href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
                  brand={brand}
                />
              </div>
              <OfferPanelA {...offerPanelProps} />
            </div>

            <div className={styles.buttonLayout}>
              <Button
                variant="outline"
                onClick={() => {
                  toggleModal();
                }}
              >
                No, register only
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  continueToCheckout();
                }}
              >
                Yes, protect my appliance
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.chooseApplianceCareContainer}>
        <h5 className="text-center text-h1 text-text-headline tablet:text-h2 tablet:text-left">
          Why choose {brand} Appliance Care?
        </h5>

        <div className={styles.chooseApplianceCareArticles}>
          {chooseApplianceCareArticles.map((article, index) => (
            <ChooseApplianceCareArticle
              icon={article.icon}
              description={article.description}
              key={index}
            />
          ))}
        </div>
      </div>

      <QuoteUpsellModal
        isOpen={displayModal}
        onClose={toggleModal}
        onRegisterOnly={registerOnly}
        onContinueToCheckout={continueToCheckout}
        offerPanelProps={offerPanelProps}
      />

      <CallUsModal blocked={displayModal} quoteId={quoteData.offer.offerReference} />
    </>
  );
}
